import { useEffect, useState } from 'react';
import type { CSSProperties } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { getOptions, saveProfile, type ProfilePayload } from '../api';
import type { OptionGroup, OptionGroups, UnitPreference } from '../types';
import { setStoredUnitPref } from '../state';
import { Field } from '../components/Field';
import { Select } from '../components/Select';
import { MultiSelect } from '../components/MultiSelect';
import { UnitToggle } from '../components/UnitToggle';
import { WeightInput } from '../components/WeightInput';
import { HeightInput } from '../components/HeightInput';
import { Spinner } from '../components/Spinner';
import { REQUIRED_PROFILE_FIELDS, type RequiredProfileField } from '../lib/profileCompleteness';

type Form = {
  name: string;
  unit_preference: UnitPreference;
  primary_goal: string | null;
  sex: 'male' | 'female' | null;
  height_cm: number | null;
  weight_kg: number | null;
  target_weight_kg: number | null;
  self_reported_bf_pct: number | null;
  activity_level: string | null;
  fasting_schedule: string | null;
  meal_frequency: string | null;
  beverage_habits: string | null;
  equipment_access: string | null;
  coach_personality: string | null;
  dietary_restrictions: string[];
  health_conditions: string[];
  injuries: string[];
  allergies: string[];
};

const initialForm: Form = {
  name: '',
  unit_preference: 'metric',
  primary_goal: null,
  sex: null,
  height_cm: null,
  weight_kg: null,
  target_weight_kg: null,
  self_reported_bf_pct: null,
  activity_level: null,
  fasting_schedule: null,
  meal_frequency: null,
  beverage_habits: null,
  equipment_access: null,
  coach_personality: null,
  dietary_restrictions: [],
  health_conditions: [],
  injuries: [],
  allergies: [],
};

function toPayload(form: Form): ProfilePayload {
  const out: ProfilePayload = {
    unit_preference: form.unit_preference,
  };
  const trimmedName = form.name.trim();
  if (trimmedName) out.name = trimmedName;
  if (form.primary_goal) out.primary_goal = form.primary_goal;
  if (form.sex) out.sex = form.sex;
  if (form.height_cm !== null) out.height_cm = form.height_cm;
  if (form.weight_kg !== null) out.weight_kg = form.weight_kg;
  if (form.target_weight_kg !== null) out.target_weight_kg = form.target_weight_kg;
  if (form.self_reported_bf_pct !== null) out.self_reported_bf_pct = form.self_reported_bf_pct;
  if (form.activity_level) out.activity_level = form.activity_level;
  if (form.fasting_schedule) out.fasting_schedule = form.fasting_schedule;
  if (form.meal_frequency) out.meal_frequency = form.meal_frequency;
  if (form.beverage_habits) out.beverage_habits = form.beverage_habits;
  if (form.equipment_access) out.equipment_access = form.equipment_access;
  if (form.coach_personality) out.coach_personality = form.coach_personality;
  if (form.dietary_restrictions.length) out.dietary_restrictions = form.dietary_restrictions;
  if (form.health_conditions.length) out.health_conditions = form.health_conditions;
  if (form.injuries.length) out.injuries = form.injuries;
  if (form.allergies.length) out.allergies = form.allergies;
  return out;
}

// Human labels for REQUIRED_PROFILE_FIELDS, used in the final-submit guard's
// "please complete X" message. The field set itself is the single source of
// truth in lib/profileCompleteness — the same set BootGate and preview enforce.
const REQUIRED_FIELD_LABELS: Record<RequiredProfileField, string> = {
  sex: 'biological sex',
  height_cm: 'height',
  weight_kg: 'current weight',
  activity_level: 'day-to-day activity',
};

// Which wizard step collects each required field — used to bounce the user back
// to the right step if the final-submit guard ever finds a gap (defensive; the
// per-step Next gating means it shouldn't be reachable).
const STEP_FOR_FIELD: Record<RequiredProfileField, number> = {
  sex: 2,
  height_cm: 3,
  weight_kg: 3,
  activity_level: 5,
};

// 8 collect steps + a review step. Mirrors the project wizard's stepped shape
// (progress dots + per-step Next/Back) so onboarding feels the same.
const TOTAL_STEPS = 9;

const KG_PER_LB = 0.45359237;
const CM_PER_IN = 2.54;
const fmtWeight = (kg: number, unit: UnitPreference): string =>
  unit === 'imperial' ? `${(kg / KG_PER_LB).toFixed(1)} lb` : `${kg.toFixed(1)} kg`;
const fmtHeight = (cm: number, unit: UnitPreference): string => {
  if (unit === 'imperial') {
    const totalIn = cm / CM_PER_IN;
    const ft = Math.floor(totalIn / 12);
    const inch = Math.round(totalIn - ft * 12);
    return `${ft}'${inch}"`;
  }
  return `${cm.toFixed(0)} cm`;
};
// Resolve an option value to its human label for the review step; falls back to
// the raw value if the group/option isn't found.
const labelFor = (group: OptionGroup | undefined, value: string | null): string => {
  if (!value) return '—';
  return group?.options.find((o) => o.value === value)?.label ?? value;
};

export function Welcome() {
  const navigate = useNavigate();
  const [step, setStep] = useState<number>(1);
  const [groups, setGroups] = useState<OptionGroups | null>(null);
  const [groupsError, setGroupsError] = useState<string | null>(null);
  const [form, setForm] = useState<Form>(initialForm);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  useEffect(() => {
    getOptions()
      .then(setGroups)
      .catch((e: unknown) => setGroupsError(e instanceof Error ? e.message : 'Failed to load options'));
  }, []);

  const set = <K extends keyof Form>(key: K, value: Form[K]): void =>
    setForm((f) => ({ ...f, [key]: value }));

  // Per-step gate. Only the steps that collect a required field block their
  // Next; the rest are always advanceable. "Required" is the shared
  // REQUIRED_PROFILE_FIELDS set BootGate and POST /projects/preview enforce, so
  // a profile can't leave onboarding incomplete and 409 at the project wizard.
  const stepValid = (s: number): boolean => {
    switch (s) {
      case 2: return form.sex !== null;
      case 3: return form.height_cm !== null && form.weight_kg !== null;
      case 5: return form.activity_level !== null;
      default: return true;
    }
  };

  const goNext = (): void => {
    setSubmitError(null);
    setStep((s) => Math.min(TOTAL_STEPS, s + 1));
  };
  const goBack = (): void => {
    setSubmitError(null);
    setStep((s) => Math.max(1, s - 1));
  };

  const handleSubmit = async (): Promise<void> => {
    setSubmitError(null);
    // Defensive re-check. Per-step gating means review should be unreachable
    // with a required field unset, but if it ever is, name what's missing and
    // bounce to the earliest step that collects it rather than POST a partial
    // payload (which would 500 on the sex NOT NULL constraint or leave the
    // profile incomplete for the project preview).
    const missing = REQUIRED_PROFILE_FIELDS.filter((field) => form[field] == null);
    if (missing.length > 0) {
      const names = missing.map((field) => REQUIRED_FIELD_LABELS[field]).join(', ');
      setSubmitError(`Please complete the required fields marked *: ${names}.`);
      setStep(Math.min(...missing.map((field) => STEP_FOR_FIELD[field])));
      return;
    }
    setSubmitting(true);
    try {
      await saveProfile(toPayload(form));
      setStoredUnitPref(form.unit_preference);
      navigate('/dashboard');
    } catch (err) {
      setSubmitError(err instanceof Error ? err.message : 'Something went wrong');
    } finally {
      setSubmitting(false);
    }
  };

  if (groupsError) {
    return (
      <div className="shell">
        <div className="errorbar">Couldn't load form options: {groupsError}</div>
      </div>
    );
  }
  if (!groups) {
    return <div className="center"><Spinner /></div>;
  }

  const dotsStyle: CSSProperties = {
    display: 'flex',
    gap: 8,
    justifyContent: 'center',
    marginBottom: 20,
    color: 'var(--text-faint)',
    fontSize: 14,
  };
  const actionsRow: CSSProperties = {
    display: 'flex',
    gap: 8,
    justifyContent: 'space-between',
    marginTop: 16,
  };
  const requiredHintStyle: CSSProperties = { fontSize: 12, marginTop: 8 };

  // Footer nav for steps 1–8. Back appears from step 2 onward; the left slot is
  // a spacer on step 1 so Next stays right-aligned. `disabled` gates Next on
  // the steps that collect a required field.
  const stepNav = (onNext: () => void, disabled = false, label = 'Next') => (
    <div style={actionsRow}>
      {step > 1 ? (
        <button type="button" className="btn btn--ghost" onClick={goBack}>Back</button>
      ) : (
        <span />
      )}
      <button type="button" className="btn btn--primary" disabled={disabled} onClick={onNext}>
        {label}
      </button>
    </div>
  );

  return (
    <form className="shell welcome" onSubmit={(e) => e.preventDefault()}>
      {step === 1 ? (
        <header className="welcome__hero rise">
          <div className="brand"><span className="brand__dot" />RESLT</div>
          <h1 className="welcome__title">Tell your coach<br /><em>who you are.</em></h1>
          <p className="welcome__sub">
            A few quick steps. You can edit anything later.
          </p>
        </header>
      ) : null}

      <div aria-hidden="true" style={dotsStyle}>
        {Array.from({ length: TOTAL_STEPS }, (_, i) => i + 1).map((n) => (
          <span key={n} style={{ opacity: n <= step ? 1 : 0.4 }}>{n <= step ? '●' : '○'}</span>
        ))}
      </div>

      {step === 1 ? (
        <section className="section rise">
          <h2 className="h2">The basics</h2>
          <Field label="First name (optional)">
            <input
              className="input"
              type="text"
              placeholder="James"
              value={form.name}
              onChange={(e) => set('name', e.target.value)}
            />
          </Field>
          <Field label="Units">
            <UnitToggle value={form.unit_preference} onChange={(v) => set('unit_preference', v)} />
          </Field>
          {stepNav(goNext)}
        </section>
      ) : null}

      {step === 2 ? (
        <section className="section rise">
          <h2 className="h2">Biological sex *</h2>
          <p className="muted">Used to make body-fat estimates more accurate.</p>
          {/* Native <input type="radio"> + <label> pattern — iOS Safari handles
              label-based touch targets reliably; the previous <button>-based
              ARIA-radiogroup variant relied on synthetic role/aria-checked
              wiring that's been a source of touch-handling fragility on
              mobile WebKit. */}
          <div className="field">
            <div className="toggle">
              <label className={`toggle__btn${form.sex === 'male' ? ' toggle__btn--on' : ''}`}>
                <input
                  type="radio"
                  name="sex"
                  value="male"
                  className="toggle__radio"
                  checked={form.sex === 'male'}
                  onChange={() => set('sex', 'male')}
                />
                Male
              </label>
              <label className={`toggle__btn${form.sex === 'female' ? ' toggle__btn--on' : ''}`}>
                <input
                  type="radio"
                  name="sex"
                  value="female"
                  className="toggle__radio"
                  checked={form.sex === 'female'}
                  onChange={() => set('sex', 'female')}
                />
                Female
              </label>
            </div>
          </div>
          {!stepValid(2) ? (
            <p className="faint" style={requiredHintStyle}>Pick one to continue.</p>
          ) : null}
          {stepNav(goNext, !stepValid(2))}
        </section>
      ) : null}

      {step === 3 ? (
        <section className="section rise">
          <h2 className="h2">Your numbers</h2>
          <p className="muted">Height and current weight — the coach sizes everything off these.</p>
          <div className="grid-2">
            <HeightInput label="Height *" value_cm={form.height_cm} onChange={(v) => set('height_cm', v)} unit={form.unit_preference} />
            <WeightInput label="Current weight *" value_kg={form.weight_kg} onChange={(v) => set('weight_kg', v)} unit={form.unit_preference} />
          </div>
          {!stepValid(3) ? (
            <p className="faint" style={requiredHintStyle}>Enter both to continue.</p>
          ) : null}
          {stepNav(goNext, !stepValid(3))}
        </section>
      ) : null}

      {step === 4 ? (
        <section className="section rise">
          <h2 className="h2">Your goal</h2>
          <p className="muted">Optional — helps shape your plan. You set a specific target when you start a project.</p>
          {groups.primary_goal ? (
            <Select label="Primary goal" group={groups.primary_goal} value={form.primary_goal} onChange={(v) => set('primary_goal', v)} />
          ) : null}
          <div className="grid-2">
            <WeightInput label="Target weight" value_kg={form.target_weight_kg} onChange={(v) => set('target_weight_kg', v)} unit={form.unit_preference} />
            <Field label="Body-fat % (estimate, optional)">
              <input
                className="input"
                type="number"
                inputMode="decimal"
                min="0" max="60" step="0.1"
                placeholder="e.g. 18"
                value={form.self_reported_bf_pct ?? ''}
                onChange={(e) => set('self_reported_bf_pct', e.target.value === '' ? null : Number(e.target.value))}
              />
            </Field>
          </div>
          {stepNav(goNext)}
        </section>
      ) : null}

      {step === 5 ? (
        <section className="section rise">
          <h2 className="h2">Day-to-day activity *</h2>
          <p className="muted">How active is your typical day, outside of training?</p>
          {groups.activity_level ? (
            <Select label="Day-to-day activity *" group={groups.activity_level} value={form.activity_level} onChange={(v) => set('activity_level', v)} />
          ) : null}
          {!stepValid(5) ? (
            <p className="faint" style={requiredHintStyle}>Pick one to continue.</p>
          ) : null}
          {stepNav(goNext, !stepValid(5))}
        </section>
      ) : null}

      {step === 6 ? (
        <section className="section rise">
          <h2 className="h2">Eating habits</h2>
          <p className="muted">All optional — they help the coach schedule meals around your day.</p>
          {groups.fasting_schedule ? (
            <Select label="Fasting / eating window" group={groups.fasting_schedule} value={form.fasting_schedule} onChange={(v) => set('fasting_schedule', v)} />
          ) : null}
          {groups.meal_frequency ? (
            <Select label="How many meals per day" group={groups.meal_frequency} value={form.meal_frequency} onChange={(v) => set('meal_frequency', v)} />
          ) : null}
          {groups.beverage_habits ? (
            <Select label="Beverages" group={groups.beverage_habits} value={form.beverage_habits} onChange={(v) => set('beverage_habits', v)} />
          ) : null}
          {stepNav(goNext)}
        </section>
      ) : null}

      {step === 7 ? (
        <section className="section rise">
          <h2 className="h2">Training & coaching</h2>
          <p className="muted">Optional — where you train and the tone you respond to.</p>
          {groups.equipment_access ? (
            <Select label="Where you train" group={groups.equipment_access} value={form.equipment_access} onChange={(v) => set('equipment_access', v)} />
          ) : null}
          {groups.coach_personality ? (
            <Select label="How should your coach sound?" group={groups.coach_personality} value={form.coach_personality} onChange={(v) => set('coach_personality', v)} hint="Pick the tone that gets the best out of you." />
          ) : null}
          {stepNav(goNext)}
        </section>
      ) : null}

      {step === 8 ? (
        <section className="section rise">
          <h2 className="h2">What we should know</h2>
          <p className="muted">Optional, but it makes meal and workout suggestions safer.</p>
          {groups.dietary_restrictions ? (
            <MultiSelect label="Dietary restrictions" group={groups.dietary_restrictions} values={form.dietary_restrictions} onChange={(v) => set('dietary_restrictions', v)} />
          ) : null}
          {groups.allergies ? (
            <MultiSelect label="Allergies" group={groups.allergies} values={form.allergies} onChange={(v) => set('allergies', v)} hint="Anything here is excluded from meal suggestions." />
          ) : null}
          {groups.health_conditions ? (
            <MultiSelect label="Health conditions" group={groups.health_conditions} values={form.health_conditions} onChange={(v) => set('health_conditions', v)} />
          ) : null}
          {groups.injuries ? (
            <MultiSelect label="Injuries to work around" group={groups.injuries} values={form.injuries} onChange={(v) => set('injuries', v)} />
          ) : null}
          {stepNav(goNext, false, 'Review')}
        </section>
      ) : null}

      {step === 9 ? (
        <section className="section rise">
          <h2 className="h2">Ready?</h2>
          <p className="muted">Quick check — you can edit anything later from Settings.</p>

          <div className="card">
            <p className="eyebrow">You</p>
            <p className="muted" style={{ marginTop: 6, fontSize: 13 }}>
              {form.name.trim() ? `${form.name.trim()} · ` : ''}{form.sex === 'male' ? 'Male' : 'Female'}
            </p>
            <p className="muted" style={{ fontSize: 13 }}>
              {form.height_cm !== null ? fmtHeight(form.height_cm, form.unit_preference) : '—'}
              {' · '}
              {form.weight_kg !== null ? fmtWeight(form.weight_kg, form.unit_preference) : '—'}
              {form.target_weight_kg !== null ? ` → ${fmtWeight(form.target_weight_kg, form.unit_preference)}` : ''}
            </p>
          </div>

          <div className="card">
            <p className="eyebrow">Plan</p>
            <p className="muted" style={{ marginTop: 6, fontSize: 13 }}>
              Goal: {labelFor(groups.primary_goal, form.primary_goal)}
            </p>
            <p className="muted" style={{ fontSize: 13 }}>
              Activity: {labelFor(groups.activity_level, form.activity_level)}
            </p>
          </div>

          {submitError ? <div className="errorbar">{submitError}</div> : null}

          <div style={actionsRow}>
            <button type="button" className="btn btn--ghost" onClick={goBack}>Back</button>
            <button
              type="button"
              className="btn btn--primary"
              disabled={submitting}
              onClick={handleSubmit}
            >
              {submitting ? (<><Spinner /> Saving…</>) : 'Start coaching'}
            </button>
          </div>
          <p className="faint" style={{ fontSize: 12, textAlign: 'center', marginTop: 8 }}>
            By continuing you accept the{' '}
            <Link to="/terms" style={{ color: 'var(--accent)', textDecoration: 'underline' }}>
              Terms of Service
            </Link>.
          </p>
        </section>
      ) : null}
    </form>
  );
}
